import { useCallback, useMemo } from "react";

import { type Store } from "@moment/api-collab/differential";
import { type TopLevelQuerySpec } from "@moment/api-collab/differential/operatorsV2/QuerySpec";
import { type DatabaseTables } from "@moment/api-collab/differential/types";

import { type HydratedCell, hydrateCell } from "./HydratedCell";
import { parseLinkedRecords } from "./parseLinkedRecords";
import { type HydratedViewColumnDefs } from "./useHydratedColumnDefs";

export interface HydratedRow {
	original: unknown;
	primaryDisplayColumn: HydratedCell | undefined;
	topRightColumns: HydratedCell[];
	bottomRightColumns: HydratedCell[];
	bottomLeftColumns: HydratedCell[];
	unplacedColumns: HydratedCell[];
	allColumns: HydratedCell[];
}

function hydrateRow(
	row: unknown,
	columnDefs: HydratedViewColumnDefs,
	store: Store<DatabaseTables> | null,
	linkedRecords: Record<string, string>,
	query: TopLevelQuerySpec,
	canEditTable: (tableId: string) => boolean
): HydratedRow {
	return {
		original: row,
		primaryDisplayColumn: columnDefs.primaryDisplayColumn
			? hydrateCell(
					row,
					columnDefs.primaryDisplayColumn,
					store,
					linkedRecords,
					query,
					canEditTable,
					columnDefs.primaryDisplayColumn.displayOptions?.readonly
				)
			: undefined,
		topRightColumns: columnDefs.topRightColumns.map(([, d]) =>
			hydrateCell(
				row,
				d,
				store,
				linkedRecords,
				query,
				canEditTable,
				d.displayOptions?.readonly
			)
		),
		bottomRightColumns: columnDefs.bottomRightColumns.map(([, d]) =>
			hydrateCell(
				row,
				d,
				store,
				linkedRecords,
				query,
				canEditTable,
				d.displayOptions?.readonly
			)
		),
		bottomLeftColumns: columnDefs.bottomLeftColumns.map(([, d]) =>
			hydrateCell(
				row,
				d,
				store,
				linkedRecords,
				query,
				canEditTable,
				d.displayOptions?.readonly
			)
		),
		unplacedColumns: columnDefs.unplacedColumns.map(([, d]) =>
			hydrateCell(
				row,
				d,
				store,
				linkedRecords,
				query,
				canEditTable,
				d.displayOptions?.readonly
			)
		),
		allColumns: columnDefs.allColumns.map((d) =>
			hydrateCell(
				row,
				d,
				store,
				linkedRecords,
				query,
				canEditTable,
				d.displayOptions?.readonly
			)
		),
	};
}

export const useHydratedRows = (
	data: unknown[],
	query: TopLevelQuerySpec | undefined,
	columnDefs: HydratedViewColumnDefs,
	store: Store<DatabaseTables> | null,
	userId: string | undefined
): HydratedRow[] => {
	const linkedRecords = useMemo(() => (query ? parseLinkedRecords(query) : {}), [query]);
	const canEditTable = useCallback(
		(tableId: string) => {
			if (!store) return false;
			return store.userHasStoragePermission("writer_own")(tableId, userId);
		},
		[store, userId]
	);
	return useMemo(
		() =>
			query
				? data.map((row) =>
						hydrateRow(row, columnDefs, store, linkedRecords, query, canEditTable)
					)
				: [],
		[data, columnDefs, store, linkedRecords, query, canEditTable]
	);
};
